import React, { useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronUp, Edit2, ExternalLink, Library, MessageCircle, Plus, RotateCcw, Search, Star, Trash2, X } from 'lucide-react';
import { CATEGORIES } from '@/data';
import { ClipThumb } from '@/components/ClipThumb';
import { d1Get, d1Upsert } from '@/lib/d1-storage';
import { generateId, now } from '@/lib/utils';
import {
  STATUS_CHIPS, VERDICTS, ageLabel, averageQuality, complianceFlags, genderLabel, getSubmissionReviews,
  isDecided, reviewerLabel, suggestedCategoryIds, verdictTally,
} from '@/lib/submissions';
import type { Clip, CoupleSubmission, Creator, Reviewer, SubmittedClip, SubmittedClipReview } from '@/types';

interface SubmissionsBoardProps {
  submissions: SubmittedClip[];
  creators: Creator[];
  reviewers: Reviewer[];
  libraryClips: Clip[];
  saveSubmissions?: (submissions: SubmittedClip[]) => Promise<void>;
  saveLibraryClips: (clips: Clip[]) => Promise<void>;
}

type View = 'decide' | 'waiting' | 'decided' | 'all';
type Decision = 'Approved' | 'Needs Revision' | 'Rejected';

interface SubmissionForm {
  clipTitle: string;
  contentLink: string;
  product: string;
  productsTaking: string;
  gender: 'male' | 'female' | 'couple';
  standoutNotes: string;
}

const EMPTY_FORM: SubmissionForm = { clipTitle: '', contentLink: '', product: '', productsTaking: '', gender: 'male', standoutNotes: '' };

// Couple uploads also live in couple_submissions (same id), which is what the couple sees
const COUPLE_STATUS: Record<Decision, CoupleSubmission['status']> = {
  Approved: 'approved',
  'Needs Revision': 'needs_work',
  Rejected: 'reviewed',
};

const REACTION_LABELS: [keyof SubmittedClipReview, string][] = [
  ['enjoyedIt', 'Enjoyed'],
  ['arousedThem', 'Aroused'],
  ['wouldTryProduct', 'Would try product'],
  ['wouldShowPartner', 'Show partner'],
  ['wouldShareWithFriend', 'Share with friend'],
  ['wouldPayToView', 'Pay to view'],
  ['wantMoreFromCreator', 'Want more'],
];

const inputClass = 'w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm';

const SubmissionFields: React.FC<{ form: SubmissionForm; onChange: (form: SubmissionForm) => void }> = ({ form, onChange }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    <label className="block">
      <span className="block text-zinc-400 text-xs mb-1">Title *</span>
      <input type="text" value={form.clipTitle} onChange={e => onChange({ ...form, clipTitle: e.target.value })} className={inputClass} placeholder="Clip title" />
    </label>
    <label className="block">
      <span className="block text-zinc-400 text-xs mb-1">Content link *</span>
      <input type="text" value={form.contentLink} onChange={e => onChange({ ...form, contentLink: e.target.value })} className={inputClass} placeholder="https://..." />
    </label>
    <label className="block">
      <span className="block text-zinc-400 text-xs mb-1">Product</span>
      <input type="text" value={form.product} onChange={e => onChange({ ...form, product: e.target.value })} className={inputClass} placeholder="e.g. Load Boost" />
    </label>
    <label className="block">
      <span className="block text-zinc-400 text-xs mb-1">Products taking</span>
      <input type="text" value={form.productsTaking} onChange={e => onChange({ ...form, productsTaking: e.target.value })} className={inputClass} placeholder="e.g. Load Boost 2 weeks" />
    </label>
    <label className="block">
      <span className="block text-zinc-400 text-xs mb-1">Who's in it</span>
      <select value={form.gender} onChange={e => onChange({ ...form, gender: e.target.value as SubmissionForm['gender'] })} className={inputClass}>
        <option value="male">Male</option>
        <option value="female">Female</option>
        <option value="couple">Couple</option>
      </select>
    </label>
    <label className="block md:col-span-2">
      <span className="block text-zinc-400 text-xs mb-1">What stands out *</span>
      <textarea value={form.standoutNotes} onChange={e => onChange({ ...form, standoutNotes: e.target.value })} className={`${inputClass} resize-none`} rows={2} />
    </label>
  </div>
);

const ReviewDetail: React.FC<{ review: SubmittedClipReview; reviewers: Reviewer[] }> = ({ review, reviewers }) => {
  const flags = complianceFlags(review);
  const quotes: [string, string | undefined][] = [
    ['Notes', review.notes],
    ['How it made them feel', review.howItMadeThemFeel],
    ['What intrigued them', review.intrigues],
    ['Revisions requested', review.revisionRequests],
    ['Suggestions for the creator', review.creatorSuggestions],
    ['After reading other reviews', review.postRevealNotes],
  ];
  return (
    <div className="bg-zinc-800/70 rounded-xl p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-white">{reviewerLabel(reviewers, review.reviewerId)}</span>
        {review.verdict ? (
          <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold ${VERDICTS[review.verdict].chip}`}>{VERDICTS[review.verdict].label}</span>
        ) : (
          <span className="px-2 py-0.5 rounded-full border border-zinc-600 text-xs text-zinc-400">No verdict</span>
        )}
        {review.qualityRating > 0 && <span className="text-xs text-yellow-400">{'★'.repeat(review.qualityRating)}<span className="text-zinc-600">{'★'.repeat(5 - review.qualityRating)}</span></span>}
        {flags.map(f => <span key={f} className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 text-xs">{f}</span>)}
        {review.postRevealChangedMind && review.postRevealChangedMind !== 'same' && (
          <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 text-xs">Changed mind after reveal</span>
        )}
        <span className="ml-auto text-xs text-zinc-500">{ageLabel(review.reviewedAt)}</span>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-400">
        {REACTION_LABELS.map(([key, label]) => {
          const value = review[key];
          return typeof value === 'string' && value ? <span key={key}>{label}: <span className="text-zinc-200">{value.replace(/-/g, ' ')}</span></span> : null;
        })}
      </div>
      {quotes.filter(([, text]) => text?.trim()).map(([label, text]) => (
        <p key={label} className="text-sm text-zinc-300"><span className="text-xs text-zinc-500">{label}: </span>{text}</p>
      ))}
      {(review.suggestedCategories?.length ?? 0) > 0 && (
        <p className="text-xs text-zinc-400">
          Fits: {review.suggestedCategories!.map(id => CATEGORIES.find(c => c.id === id)?.name ?? id).join(', ')}
        </p>
      )}
    </div>
  );
};

export const SubmissionsBoard: React.FC<SubmissionsBoardProps> = ({ submissions, creators, reviewers, libraryClips, saveSubmissions, saveLibraryClips }) => {
  const [view, setView] = useState<View>('decide');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [reopened, setReopened] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState<SubmissionForm>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<SubmissionForm>(EMPTY_FORM);
  const [promoteCategory, setPromoteCategory] = useState<Record<string, string>>({});

  const groups = useMemo(() => {
    const decide = submissions.filter(s => !isDecided(s) && getSubmissionReviews(s).length > 0);
    const waiting = submissions.filter(s => !isDecided(s) && getSubmissionReviews(s).length === 0);
    const decided = submissions.filter(isDecided);
    return { decide, waiting, decided, all: submissions };
  }, [submissions]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = groups[view].filter(s => !term || [s.clipTitle, s.product, s.creatorId].some(v => v?.toLowerCase().includes(term)));
    const time = (s: SubmittedClip) => new Date(s.dateSubmitted).getTime() || 0;
    if (view === 'decide') return [...list].sort((a, b) => getSubmissionReviews(b).length - getSubmissionReviews(a).length || time(a) - time(b));
    if (view === 'waiting') return [...list].sort((a, b) => time(a) - time(b));
    return [...list].sort((a, b) => time(b) - time(a));
  }, [groups, view, search]);

  const run = async (id: string, action: () => Promise<void>) => {
    setBusyId(id);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  // Patch the latest saved row, not our possibly-stale copy, so a reviewer's save made
  // while this screen was open (questions, gender) isn't overwritten.
  const updateSubmission = async (id: string, patch: Partial<SubmittedClip>) => {
    if (!saveSubmissions) return;
    const local = submissions.find(s => s.id === id);
    const latest = await d1Get<SubmittedClip>('submitted_clips', id).catch(() => null);
    const { reviews: _reviews, ...row } = { ...(latest ?? local), ...patch } as SubmittedClip;
    await d1Upsert('submitted_clips', row as unknown as Record<string, unknown>);
    await saveSubmissions(submissions.map(s => (s.id === id ? { ...row, reviews: s.reviews } : s)));
  };

  // Keep the couple's own view of their upload in step
  const syncCoupleCopy = async (submission: SubmittedClip, status: CoupleSubmission['status']) => {
    if (submission.gender !== 'couple' && !submission.creatorId.startsWith('VBC-')) return;
    const coupleCopy = await d1Get<CoupleSubmission>('couple_submissions', submission.id).catch(() => null);
    if (coupleCopy) await d1Upsert('couple_submissions', { ...coupleCopy, status, reviewedAt: now() });
  };

  // Moving a library clip away from Approved: offer to take it back out of the library
  const libraryPatch = async (submission: SubmittedClip): Promise<Partial<SubmittedClip>> => {
    const clipId = submission.promotedToClipId;
    if (!clipId || !libraryClips.some(c => c.id === clipId)) return {};
    if (!confirm(`“${submission.clipTitle}” is in the Date Night library. Remove it from the library too?`)) return {};
    await saveLibraryClips(libraryClips.filter(c => c.id !== clipId));
    return { promotedToClipId: undefined };
  };

  const decide = (submission: SubmittedClip, status: Decision) => run(submission.id, async () => {
    const patch = status === 'Approved' ? {} : await libraryPatch(submission);
    await updateSubmission(submission.id, { ...patch, status });
    setReopened(null);
    await syncCoupleCopy(submission, COUPLE_STATUS[status]);
  });

  // Undo a decision entirely: back into the reviewers' queue
  const reopenForReview = (submission: SubmittedClip) => run(submission.id, async () => {
    const hasReviews = getSubmissionReviews(submission).length > 0;
    const patch = await libraryPatch(submission);
    await updateSubmission(submission.id, { ...patch, status: hasReviews ? 'Under Review' : 'Submitted' });
    setReopened(null);
    await syncCoupleCopy(submission, hasReviews ? 'reviewed' : 'pending');
  });

  const promote = (submission: SubmittedClip, categoryId: string) => run(submission.id, async () => {
    if (!saveSubmissions) return;
    const clip: Clip = {
      id: generateId('clip'),
      categoryId,
      title: submission.clipTitle,
      link: submission.contentLink,
      description: submission.standoutNotes || `Creator submission for ${submission.product}`,
      tags: submission.product ? [submission.product.toLowerCase().replace(/\s+/g, '-')] : [],
      rating: '',
      notesForHim: '',
      notesForHer: '',
      reviews: [],
      picks: [],
    };
    await saveLibraryClips([...libraryClips, clip]);
    await updateSubmission(submission.id, { promotedToClipId: clip.id });
  });

  const addSubmission = () => run('new', async () => {
    if (!saveSubmissions) return;
    const submission: SubmittedClip = {
      id: generateId('sub'),
      clipTitle: addForm.clipTitle.trim(),
      contentLink: addForm.contentLink.trim(),
      creatorId: 'admin',
      product: addForm.product.trim(),
      productsTaking: addForm.productsTaking.trim() || undefined,
      gender: addForm.gender,
      status: 'Submitted',
      dateSubmitted: now(),
      standoutNotes: addForm.standoutNotes.trim(),
    };
    await saveSubmissions([submission, ...submissions]);
    setAdding(false);
    setAddForm(EMPTY_FORM);
    setView('waiting');
  });

  const saveEdit = (submission: SubmittedClip) => run(submission.id, async () => {
    await updateSubmission(submission.id, {
      clipTitle: editForm.clipTitle.trim(),
      contentLink: editForm.contentLink.trim(),
      product: editForm.product.trim(),
      productsTaking: editForm.productsTaking.trim() || undefined,
      gender: editForm.gender,
      standoutNotes: editForm.standoutNotes.trim(),
    });
    setEditingId(null);
  });

  const remove = (submission: SubmittedClip) => {
    if (!confirm(`Delete “${submission.clipTitle}”?`)) return;
    run(submission.id, async () => {
      if (saveSubmissions) await saveSubmissions(submissions.filter(s => s.id !== submission.id));
    });
  };

  const formValid = (f: SubmissionForm) => !!(f.clipTitle.trim() && f.contentLink.trim() && f.standoutNotes.trim());

  const tabs: { id: View; label: string; hint: string }[] = [
    { id: 'decide', label: 'Needs your decision', hint: 'Reviewed, waiting on you' },
    { id: 'waiting', label: 'Waiting on reviewers', hint: 'No reviews yet' },
    { id: 'decided', label: 'Decided', hint: 'Approved, revise or rejected' },
    { id: 'all', label: 'All', hint: 'Everything' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-display text-3xl font-medium text-white">Submissions</h3>
          <p className="text-sm text-zinc-400">Reviewers recommend; you make the final call and add winners to the library.</p>
        </div>
        <button
          onClick={() => { setAdding(v => !v); setAddForm(EMPTY_FORM); }}
          className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-action text-ground text-sm font-bold"
        >
          <Plus className="w-4 h-4" /> Add submission
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setView(t.id)}
            aria-pressed={view === t.id}
            className={`text-left rounded-2xl border p-4 transition-colors ${view === t.id ? 'bg-zinc-700/60 border-zinc-500' : 'bg-zinc-900/60 border-zinc-700 hover:border-zinc-600'}`}
          >
            <span className={`block text-2xl font-bold ${t.id === 'decide' && groups.decide.length > 0 ? 'text-attention' : 'text-white'}`}>{groups[t.id].length}</span>
            <span className="block text-sm font-semibold text-zinc-200">{t.label}</span>
            <span className="block text-xs text-zinc-500">{t.hint}</span>
          </button>
        ))}
      </div>

      <label className="relative block">
        <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <span className="sr-only">Search submissions</span>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by title, product or creator"
          className="w-full bg-zinc-900/60 border border-zinc-700 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-zinc-500"
        />
      </label>

      {error && <p className="text-sm text-red-400" role="alert">Couldn’t save: {error}</p>}

      {adding && (
        <div className="bg-zinc-900 rounded-2xl p-4 border border-action/40 space-y-3">
          <h4 className="text-white font-semibold">New submission</h4>
          <SubmissionFields form={addForm} onChange={setAddForm} />
          <div className="flex gap-2">
            <button onClick={addSubmission} disabled={!formValid(addForm) || busyId === 'new'} className="px-4 py-2 rounded-lg bg-action text-ground text-sm font-bold disabled:opacity-50">
              Add submission
            </button>
            <button onClick={() => setAdding(false)} className="px-4 py-2 rounded-lg bg-zinc-700 text-zinc-300 text-sm">Cancel</button>
          </div>
        </div>
      )}

      {visible.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          {search ? 'Nothing matches that search.' : view === 'decide' ? 'Nothing waiting on your decision.' : view === 'waiting' ? 'Every submission has at least one review.' : 'No submissions here yet.'}
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map(submission => {
            const reviews = getSubmissionReviews(submission);
            const tally = verdictTally(reviews);
            const quality = averageQuality(reviews);
            const flagged = reviews.filter(r => complianceFlags(r).length > 0).length;
            const questions = submission.reviewerQuestions || [];
            const creator = creators.find(c => c.id === submission.creatorId);
            const decided = isDecided(submission);
            const showDecision = !decided || reopened === submission.id;
            const isOpen = expanded === submission.id;
            const busy = busyId === submission.id;
            const categoryChoice = promoteCategory[submission.id] ?? suggestedCategoryIds(reviews)[0] ?? CATEGORIES[0]!.id;
            const who = submission.gender === 'couple' || submission.creatorId.startsWith('VBC-')
              ? `Couple ${submission.creatorId}`
              : creator ? `Order ${creator.orderId}` : submission.creatorId;

            if (editingId === submission.id) {
              return (
                <div key={submission.id} className="bg-zinc-900 rounded-2xl p-4 border border-blue-500/40 space-y-3">
                  <h4 className="text-white font-semibold">Edit submission</h4>
                  <SubmissionFields form={editForm} onChange={setEditForm} />
                  <div className="flex gap-2">
                    <button onClick={() => saveEdit(submission)} disabled={!formValid(editForm) || busy} className="px-4 py-2 rounded-lg bg-action text-ground text-sm font-bold disabled:opacity-50">Save</button>
                    <button onClick={() => setEditingId(null)} className="px-4 py-2 rounded-lg bg-zinc-700 text-zinc-300 text-sm">Cancel</button>
                  </div>
                </div>
              );
            }

            return (
              <article key={submission.id} className="bg-zinc-900/60 rounded-2xl border border-zinc-700 overflow-hidden">
                <div className="flex flex-col sm:flex-row gap-4 p-4">
                  <a href={submission.contentLink} target="_blank" rel="noopener noreferrer" className="shrink-0 self-stretch sm:self-start" aria-label={`Open ${submission.clipTitle}`}>
                    <ClipThumb url={submission.contentLink} className="w-full sm:w-32 h-40 sm:h-32" showPlay />
                  </a>

                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold ${STATUS_CHIPS[submission.status]}`}>
                        {decided ? submission.status : reviews.length > 0 ? 'Awaiting your decision' : 'Awaiting reviews'}
                      </span>
                      {submission.promotedToClipId && (
                        <span className="px-2 py-0.5 rounded-full bg-action-tint text-action text-xs font-semibold flex items-center gap-1"><Library className="w-3 h-3" /> In library</span>
                      )}
                      <span className="text-xs text-zinc-500">Submitted {ageLabel(submission.dateSubmitted)}</span>
                    </div>
                    <h4 className="text-lg font-semibold text-white leading-snug">{submission.clipTitle}</h4>
                    <p className="text-sm text-zinc-400">
                      {[submission.product, genderLabel(submission.gender), who].filter(Boolean).join(' · ')}
                    </p>

                    <div className="flex flex-wrap items-center gap-2">
                      {reviews.length === 0 ? (
                        <span className="text-xs text-zinc-500">No reviews yet</span>
                      ) : (
                        <>
                          {(['approve', 'revise', 'reject'] as const).map(v => tally[v] > 0 && (
                            <span key={v} className={`px-2 py-0.5 rounded-full border text-xs font-semibold ${VERDICTS[v].chip}`}>{tally[v]} {VERDICTS[v].short.toLowerCase()}</span>
                          ))}
                          {tally.none > 0 && <span className="px-2 py-0.5 rounded-full border border-zinc-600 text-xs text-zinc-400">{tally.none} no verdict</span>}
                          {quality !== null && <span className="flex items-center gap-1 text-xs text-yellow-400"><Star className="w-3.5 h-3.5 fill-current" /> {quality}</span>}
                          {flagged > 0 && <span className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 text-xs font-semibold">{flagged} compliance flag{flagged === 1 ? '' : 's'}</span>}
                        </>
                      )}
                      {questions.length > 0 && (
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 text-xs"><MessageCircle className="w-3 h-3" /> {questions.length} question{questions.length === 1 ? '' : 's'}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex sm:flex-col gap-1 self-start">
                    <button
                      onClick={() => {
                        setEditingId(submission.id);
                        setEditForm({
                          clipTitle: submission.clipTitle,
                          contentLink: submission.contentLink,
                          product: submission.product,
                          productsTaking: submission.productsTaking || '',
                          gender: submission.gender || 'male',
                          standoutNotes: submission.standoutNotes || '',
                        });
                      }}
                      className="p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-zinc-800"
                      aria-label="Edit submission"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(submission)} className="p-2 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-zinc-800" aria-label="Delete submission">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Decision bar */}
                <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-t border-zinc-800 bg-zinc-900/60">
                  {showDecision ? (
                    <>
                      <span className="text-xs text-zinc-500 mr-1">Decision:</span>
                      <button disabled={busy} onClick={() => decide(submission, 'Approved')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-action text-ground text-sm font-bold disabled:opacity-50">
                        <Check className="w-4 h-4" /> Approve
                      </button>
                      <button disabled={busy} onClick={() => decide(submission, 'Needs Revision')} className="px-3 py-1.5 rounded-lg border border-orange-500/40 text-orange-300 text-sm font-semibold hover:bg-orange-500/10 disabled:opacity-50">
                        Needs revisions
                      </button>
                      <button disabled={busy} onClick={() => decide(submission, 'Rejected')} className="px-3 py-1.5 rounded-lg border border-red-500/40 text-red-300 text-sm font-semibold hover:bg-red-500/10 disabled:opacity-50">
                        Reject
                      </button>
                      {reopened === submission.id && (
                        <>
                          <button disabled={busy} onClick={() => reopenForReview(submission)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-zinc-300 text-sm hover:text-white hover:bg-zinc-800 disabled:opacity-50">
                            <RotateCcw className="w-3.5 h-3.5" /> Back to reviewers
                          </button>
                          <button onClick={() => setReopened(null)} className="p-1.5 text-zinc-500 hover:text-white" aria-label="Cancel changing decision"><X className="w-4 h-4" /></button>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {submission.status === 'Approved' && !submission.promotedToClipId && (
                        <>
                          <label className="flex items-center gap-2 text-xs text-zinc-400">
                            Add to library in
                            <select
                              value={categoryChoice}
                              onChange={e => setPromoteCategory(prev => ({ ...prev, [submission.id]: e.target.value }))}
                              className="bg-zinc-800 border border-zinc-600 rounded-lg px-2 py-1.5 text-sm text-white"
                            >
                              {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
                            </select>
                          </label>
                          <button disabled={busy} onClick={() => promote(submission, categoryChoice)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-action text-ground text-sm font-bold disabled:opacity-50">
                            <Library className="w-4 h-4" /> Add to library
                          </button>
                        </>
                      )}
                      <button onClick={() => setReopened(submission.id)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-zinc-400 text-sm hover:text-white hover:bg-zinc-800">
                        <RotateCcw className="w-3.5 h-3.5" /> Change decision
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setExpanded(isOpen ? null : submission.id)}
                    aria-expanded={isOpen}
                    className="ml-auto flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-zinc-300 hover:bg-zinc-800"
                  >
                    {reviews.length > 0 ? `Reviews (${reviews.length})` : 'Details'} {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>

                {isOpen && (
                  <div className="px-4 pb-4 pt-3 space-y-3 border-t border-zinc-800">
                    {(submission.standoutNotes || submission.productsTaking) && (
                      <div className="text-sm text-zinc-300 space-y-1">
                        {submission.standoutNotes && <p><span className="text-xs text-zinc-500">Creator says: </span>{submission.standoutNotes}</p>}
                        {submission.productsTaking && <p><span className="text-xs text-zinc-500">Taking: </span>{submission.productsTaking}</p>}
                        <a href={submission.contentLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-400 hover:underline">
                          Open original <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    )}
                    {reviews.map((review, i) => <ReviewDetail key={`${review.reviewerId}-${i}`} review={review} reviewers={reviewers} />)}
                    {questions.length > 0 && (
                      <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-3 space-y-2">
                        <p className="text-xs font-semibold text-blue-300">Questions reviewers asked the creator</p>
                        {questions.map(q => (
                          <div key={q.id} className="text-sm">
                            <p className="text-zinc-200">{q.question}</p>
                            <p className="text-xs text-zinc-500">
                              {reviewerLabel(reviewers, q.reviewerId)} · {ageLabel(q.askedAt)}{q.reply ? '' : ' · no reply yet'}
                            </p>
                            {q.reply && <p className="mt-1 pl-3 border-l-2 border-blue-500/40 text-blue-200">{q.reply}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};
